import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function MedicationAlertsModal({ serverId, serviceUserId, onClose }) {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchAlerts = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'medicationAlerts');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    // Unresolved first
                    const statusA = a.resolved ? 1 : 0;
                    const statusB = b.resolved ? 1 : 0;
                    if (statusA !== statusB) return statusA - statusB;

                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.alertDate || a.date || a.createdAt);
                    const dateB = getMs(b.alertDate || b.date || b.createdAt);
                    return dateB - dateA; // Newest first
                });
                
                setAlerts(loaded);
            } catch (err) {
                console.error("Error fetching medication alerts:", err);
                setError("Failed to load medication alerts.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchAlerts();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Medication Alerts</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Missed, refused, or PRN medication alerts</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading alerts...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : alerts.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔔</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Alerts</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no medication alerts for this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {alerts.map((alertItem, index) => {
                                const type = alertItem.alertType || alertItem.type || 'Medication Alert';
                                const medication = alertItem.medicationName || alertItem.medication || 'Unknown Medication';
                                const isResolved = alertItem.resolved === true || alertItem.status === 'resolved';
                                
                                return (
                                    <div key={alertItem.id || index} style={{...styles.recordCard, opacity: isResolved ? 0.7 : 1}}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{type}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    Medication: {medication}
                                                </div>
                                            </div>
                                            <span style={{ 
                                                display: 'inline-block', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 'bold',
                                                backgroundColor: isResolved ? '#dcfce7' : '#fee2e2', 
                                                color: isResolved ? '#166534' : '#991b1b', 
                                                border: `1px solid ${isResolved ? '#bbf7d0' : '#fecaca'}`
                                            }}>
                                                {isResolved ? 'RESOLVED' : 'ACTION REQUIRED'}
                                            </span>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Date/Time</label>
                                                <div style={styles.fieldValue}>{alertItem.alertDate || alertItem.date || alertItem.createdAt ? new Date((alertItem.alertDate || alertItem.date || alertItem.createdAt)?.seconds ? (alertItem.alertDate || alertItem.date || alertItem.createdAt).seconds * 1000 : (alertItem.alertDate || alertItem.date || alertItem.createdAt)).toLocaleString() : 'N/A'}</div>
                                            </div>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Reported By</label>
                                                <div style={styles.fieldValue}>{alertItem.reportedBy || alertItem.author || 'System'}</div>
                                            </div>
                                        </div>
                                        
                                        {(alertItem.notes || alertItem.reason || alertItem.details) && (
                                            <div style={{ marginTop: '16px' }}>
                                                <label style={styles.fieldLabel}>Notes / Reason</label>
                                                <div style={{ fontSize: '13px', color: '#4b5563', whiteSpace: 'pre-wrap' }}>{alertItem.notes || alertItem.reason || alertItem.details}</div>
                                            </div>
                                        )}
                                        
                                        {!isResolved && (
                                            <div style={{ marginTop: '16px' }}>
                                                <button style={styles.viewBtn} onClick={() => alert('Resolve Action: Coming to React V2 soon.')}>
                                                    Resolve Alert
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' },
    viewBtn: { width: '100%', padding: '10px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.2s' }
};
